<template>
  <button class="conn-pill" :class="pillClass" :title="title" @click="open">
    <span v-if="syncing" class="spinner" />
    <span v-else class="dot" :class="pillClass" />
    <span class="label">{{ label }}</span>
    <span v-if="offlineQueue.totalUnsynced > 0" class="badge">{{ offlineQueue.totalUnsynced }}</span>
  </button>
</template>

<script setup>
import { computed } from 'vue';
import { useOnlineStatus } from '@/composables/useOnlineStatus';
import { useOfflineQueueStore } from '@/stores/offlineQueueStore';

// Connection is binary: online or offline. The badge counts locally-queued requests.
// When online with queued work, the pill shows a "Syncing" animation (cooldown ticking
// or a POST in flight) — independent of whether anyone is logged in.
const { isOnline } = useOnlineStatus();
const offlineQueue = useOfflineQueueStore();

const syncing = computed(() => isOnline.value && offlineQueue.isSyncing);

const pillClass = computed(() => {
  if (!isOnline.value) return 'pill--offline';
  if (syncing.value) return 'pill--syncing';
  return 'pill--online';
});

const label = computed(() => {
  if (!isOnline.value) return 'Offline';
  if (offlineQueue.isSaving) return 'Syncing…';
  if (offlineQueue.syncCountdown != null) return 'Sync queued';
  return 'Online';
});

const title = computed(() => {
  if (!isOnline.value) return 'Offline — orders are saved on this device. Click to view the queue.';
  if (syncing.value) return 'Syncing offline transactions. Click for details.';
  return 'Connected';
});

function open() { offlineQueue.syncModalOpen = true; }
</script>

<style scoped>
.conn-pill {
  position: fixed; top: 0.6rem; right: 0.6rem; z-index: 9999;
  display: inline-flex; align-items: center; gap: 0.4rem;
  padding: 0.25rem 0.7rem; border-radius: 999px; border: none;
  font-size: 0.75rem; font-weight: 600; cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15); user-select: none;
  transition: background 0.2s, color 0.2s;
}
.pill--online   { background: #dcfce7; color: #166534; }
.pill--offline  { background: #fee2e2; color: #991b1b; }
.pill--syncing  { background: #ede9fe; color: #5b21b6; }

.dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.dot.pill--online  { background: #16a34a; }
.dot.pill--offline { background: #dc2626; animation: pulse 1.4s infinite; }

.spinner {
  width: 10px; height: 10px; flex-shrink: 0; border-radius: 50%;
  border: 2px solid #c4b5fd; border-top-color: #7c3aed;
  animation: spin 0.7s linear infinite;
}

.badge { background: #dc2626; color: #fff; border-radius: 999px;
  padding: 0 0.4rem; font-size: 0.7rem; min-width: 1.1rem; text-align: center; }

@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
@keyframes spin { to { transform: rotate(360deg); } }
</style>
